import React, { Fragment, useContext, useState, useEffect } from "react";
import { AuthContext } from "../contexts/auth";
import { Dialog, Transition } from "@headlessui/react";
import { listOrganizationalUnits } from "../services/api_org_unit";
import moment from "moment";
import {ExportInouts} from "../services/api_extractions";
import Swal from "sweetalert2";
import { Link } from "wouter";



export default function ModalExtractionInouts({ show, onClose, selectedUnit, selectedEmployees, defaultStartDate, defaultEndDate }) {
    const { token, profile } = useContext(AuthContext);

    const [units, setUnits] = useState([]);
    const [isLoadingOu, setIsLoadingOu] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState(false);
    const [includeTimbrature, setIncludeTimbrature] = useState(true);
    const [includeShifts, setIncludeShifts] = useState(true);
    const [includeJustifications, setIncludeJustifications] = useState(true);
    const [startDate, setStartDate] = useState(defaultStartDate || "");
    const [endDate, setEndDate] = useState(defaultEndDate || "");
    const areDatesSelected = Boolean(startDate && endDate);
    
    
    const isEmployee = profile.customer_type === 'employee';
    const hasManageJustificationPermission =
        profile.employee?.permissions?.includes('hr.manage_justification_ou') ||
        profile.employee?.permissions?.includes('hr.manage_justification_all');

    const hasViewShiftPermission =
        profile.employee?.permissions?.includes('hr.view_shifts_ou') ||
        profile.employee?.permissions?.includes('hr.view_shifts_all') ||
        profile.employee?.permissions?.includes('hr.manage_shifts_ou') ||
        profile.employee?.permissions?.includes('hr.manage_shifts_all');
    
    const hasInOutsPermission = 
        profile.employee?.permissions?.includes('hr.view_inouts_ou') ||
        profile.employee?.permissions?.includes('hr.view_inouts_all') ||
        profile.employee?.permissions?.includes('hr.manage_inouts_ou') ||
        profile.employee?.permissions?.includes('hr.manage_inouts_all');

    const canExportInouts = !isEmployee || hasInOutsPermission;
    const canExportShifts = !isEmployee || hasViewShiftPermission;
    const canExportJustifications = !isEmployee || hasManageJustificationPermission;

    useEffect(() => {
        const fetchUnits = async () => {
        setIsLoadingOu(true);
        try {
            const res = await listOrganizationalUnits(token, true, null, "shifts");
            setUnits(res.data || []);
        } catch (err) {
            console.error("Errore nel recupero delle unità organizzative:", err);
        } finally {
            setIsLoadingOu(false);
        }
    };

        if (show) fetchUnits();
    }, [show, token]);


    const handleDateChange = (newStartDate, newEndDate) => {
        const formattedStartDate = newStartDate ? moment(newStartDate).format("YYYY-MM-DD") : null;
        const formattedEndDate = newEndDate ? moment(newEndDate).format("YYYY-MM-DD") : null;
    
        setStartDate(formattedStartDate);
        setEndDate(formattedEndDate);
    };

    const handleExport = async () => {
        if (!startDate || !endDate) {
            Swal.fire({
                icon: 'warning',
                title: 'Intervallo di date non valido',
                text: 'Seleziona un intervallo di date valido prima di procedere.',
            });
            return;
        }

        if (moment(endDate).isBefore(moment(startDate))) {
            Swal.fire({
                icon: 'warning',
                title: 'Intervallo non valido',
                text: 'La data di fine deve essere successiva alla data di inizio.',
            });
            return;
        }

        const start = moment(startDate).startOf('day');
        const end = moment(endDate).startOf('day');
        const days = end.diff(start, 'days') + 1;

        if (days > 31) {
            Swal.fire({
            icon: 'warning',
            title: 'Intervallo date non consentito',
            html: `L'intervallo massimo per l'estrazione è di <strong>31 giorni</strong>.<br/>
                    Intervallo selezionato: <strong>${days} giorni</strong>.`,
            });
            return;
        }

        const employeeIds = selectedEmployees.map(e => e.id);
        const unitId = selectedUnit ? selectedUnit.id : '';

        setIsDownloading(true);
        setDownloadError(null);

        try {
            // crea la richiesta (FileDownload + celery)
            const { status, data } = await ExportInouts(
                token,
                startDate,
                endDate,
                employeeIds,
                unitId,
                canExportInouts ? includeTimbrature : false,
                canExportShifts ? includeShifts : false,
                canExportJustifications ? includeJustifications : false
            );

            const alreadyExists =
                status === 200 &&
                data?.detail?.toLowerCase().includes("già");

                Swal.fire({
                icon: alreadyExists ? "warning" : "success",
                title: alreadyExists
                    ? "Esportazione già in corso"
                    : "Esportazione avviata",
                html: `
                    <div>
                    ${
                        alreadyExists
                        ? "Esiste già un export per gli stessi parametri."
                        : "Il file verrà generato in background e sarà scaricabile quando pronto."
                    }
                    <br/>
                    <a href="/download_list" style="color:#4f46e5; text-decoration:underline;">
                        Clicca qui
                    </a>
                     per andare alla pagina download.
                    </div>
                `,
                confirmButtonText: "Chiudi",
            });

            onClose();

        } catch (error) {
            const errorMessage =
                error instanceof Error
                ? error.message
                : typeof error === 'string'
                ? error
                : 'Errore, riprova più tardi.';

            setDownloadError(errorMessage);

            Swal.fire({
                icon: 'error',
                title: 'Errore durante l’esportazione',
                text: errorMessage,
            });
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <Transition.Root show={show} as={Fragment}>
            <Dialog as="div" className="fixed inset-0 z-[1000] flex items-center justify-center mx-auto px-8 md:px-auto" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
                </Transition.Child>

                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                    <div className="relative w-full max-w-5xl h-50 bg-white rounded-lg shadow-xl flex flex-col justify-between overflow-hidden">
                        {/* Header */}
                        <div className="relative pb-2 pt-3 border-b border-gray-200 bg-indigo-200">
                            <Dialog.Title
                                as="h3"
                                className="text-lg font-semibold text-indigo-700 text-center"
                            >
                                Seleziona il periodo per l'esportazione
                            </Dialog.Title>

                            <button
                                onClick={onClose}
                                className="absolute right-4 top-3 text-indigo-700 hover:text-gray-500 text-xl font-bold focus:outline-none"
                                aria-label="Chiudi"
                            >
                                ×
                            </button>
                        </div>

                        {/* Body scrollabile */}
                        <div className="flex-1 overflow-y-auto px-6 pt-4 pb-2">
                            <div className="grid grid-cols-6 px-4 gap-4 pb-6">
                                <div className="col-span-1 flex items-center text-sm font-medium text-gray-900">
                                    Periodo
                                </div>

                                <div className="col-span-2">
                                    <label className="text-sm font-medium text-gray-700 block mb-1">Data Inizio</label>
                                    <input
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                                    />
                                </div>

                                <div className="col-span-2">
                                    <label className="text-sm font-medium text-gray-700 block mb-1">Data Fine</label>
                                    <input
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:ring-indigo-500"
                                    />
                                </div>
                            </div>

                            {/* Selezione tipi di dati */}
                            <div className="grid grid-cols-4 gap-4 border-t pt-4 px-6">
                                <label className="block text-sm font-medium text-gray-900 mb-2">Seleziona campi</label>
                                   {canExportInouts && (
                                        <label className="flex items-center text-sm gap-2">
                                            <input
                                                type="checkbox"
                                                checked={includeTimbrature}
                                                onChange={(e) => setIncludeTimbrature(e.target.checked)}
                                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                            />
                                            Timbrature
                                        </label>
                                    )}

                                    {canExportShifts && (
                                        <label className="flex items-center text-sm gap-2">
                                            <input
                                                type="checkbox"
                                                checked={includeShifts}
                                                onChange={(e) => setIncludeShifts(e.target.checked)}
                                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                            />
                                            Turni
                                        </label>
                                    )}

                                    {canExportJustifications && (
                                        <label className="flex items-center text-sm gap-2">
                                            <input
                                                type="checkbox"
                                                checked={includeJustifications}
                                                onChange={(e) => setIncludeJustifications(e.target.checked)}
                                                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                            />
                                            Giustificativi
                                        </label>
                                    )}
                            </div>
                                
                            
                            <div className='flex justify-center items-center py-4'>
                                <button 
                                    onClick={handleExport}
                                    className='inline-flex items-center justify-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 me-3 w-20'
                                >
                                    Esporta
                                </button>
                                <button 
                                    onClick={onClose}
                                    className='border block rounded-md bg-grey-400 px-3 py-2 mr-2 text-center text-sm font-semibold text-grey-800 shadow-sm hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-grey-600 w-20'
                                >
                                    Chiudi
                                </button>
                            </div>
                        </div>
                    </div>
                </Transition.Child>
            </Dialog>
        </Transition.Root>
    );
}